import { useState, useEffect, useRef } from 'react';
import { getVessels, deleteVessel, importVessels } from './api';
import API from './api';

const VSAD_REQUIRED = ['ship_name','imo_number','flag','ship_type','gross_tonnage','class_register','owner_manager'];
const VSAD_RECOMMENDED = ['call_sign','mmsi','port_of_registry','net_tonnage','deadweight','loa','beam','hull_type','year_of_build','owner_address'];

function vesselCompleteness(v) {
  const reqFilled = VSAD_REQUIRED.filter(f => v[f] && String(v[f]).trim()).length;
  const recFilled = VSAD_RECOMMENDED.filter(f => v[f] && String(v[f]).trim()).length;
  const total = VSAD_REQUIRED.length + VSAD_RECOMMENDED.length;
  const filled = reqFilled + recFilled;
  return {
    pct: Math.round((filled / total) * 100),
    reqFilled,
    reqTotal: VSAD_REQUIRED.length,
    missingRequired: VSAD_REQUIRED.filter(f => !v[f] || !String(v[f]).trim()),
  };
}

export default function FleetManagement({ setPage, onLogout, setEditVessel }) {
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fleetSearch, setFleetSearch] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const importInputRef = useRef(null);

  useEffect(() => {
    getVessels()
      .then(r => setVessels(r.data))
      .catch(() => onLogout())
      .finally(() => setLoading(false));
  }, []);

  const handleDeleteVessel = async (imo) => {
    if (!window.confirm('Delete this vessel?')) return;
    try {
      await deleteVessel(imo);
      const r = await getVessels();
      setVessels(r.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await importVessels(fd, overwrite);
      setImportResult(r.data);
      const v = await getVessels();
      setVessels(v.data);
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Import failed.' });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await API.get('/vessels/template/download', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vessel_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Template download failed.');
    }
  };

  if (loading) {
    return (
      <div style={s.wrapper}>
        <div style={s.topAccent} />
        <div style={s.loadingState}>Loading...</div>
      </div>
    );
  }

  const filtered = vessels.filter(v =>
    v.ship_name.toLowerCase().includes(fleetSearch.toLowerCase()) ||
    v.imo_number.includes(fleetSearch)
  );

  const uniqueFlags = [...new Set(vessels.map(v => v.flag).filter(Boolean))];
  const uniqueTypes = [...new Set(vessels.map(v => v.ship_type).filter(Boolean))];

  return (
    <div style={s.wrapper}>
      <div style={s.topAccent} />

      <header style={s.header}>
        <div style={s.headerLeft}>
          <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
          <div style={s.headerIcon}>🚢</div>
          <div>
            <div style={s.headerTitle}>FLEET MANAGEMENT</div>
            <div style={s.headerSub}>Vessel database — add, edit, import and manage vessels</div>
          </div>
        </div>
        <button style={s.logoutBtn} onClick={onLogout}>LOGOUT</button>
      </header>

      <main style={s.main}>
        <input ref={importInputRef} type="file" accept=".csv,.xlsx" style={{ display: 'none' }} onChange={handleImportFile} />

        <div style={s.topBar}>
          <div>
            <div style={s.pageTitle}>Fleet Database</div>
            <div style={s.pageSub}>{vessels.length} vessel{vessels.length !== 1 ? 's' : ''} registered</div>
          </div>
          <div style={s.topBarRight}>
            <input
              style={s.searchInput}
              placeholder="Search by name or IMO..."
              value={fleetSearch}
              onChange={e => setFleetSearch(e.target.value)}
            />
            <button style={s.templateBtn} onClick={handleDownloadTemplate}>↓ Template</button>
            <label style={s.overwriteLabel}>
              <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} style={{ marginRight: '5px' }} />
              Overwrite
            </label>
            <button
              style={{ ...s.importBtn, opacity: importing ? 0.6 : 1 }}
              onClick={() => importInputRef.current.click()}
              disabled={importing}
            >
              {importing ? '⏳ Importing...' : '↑ Import CSV/Excel'}
            </button>
            <button style={s.addBtn} onClick={() => { setEditVessel(null); setPage('vessel-form'); }}>
              + ADD VESSEL
            </button>
          </div>
        </div>

        {importResult && (
          <div style={importResult.error ? s.importError : s.importSuccess}>
            {importResult.error ? (
              <span>✗ {importResult.error}</span>
            ) : (
              <div>
                <div style={s.importSummary}>
                  ✓ Import complete — <strong>{importResult.imported}</strong> new, <strong>{importResult.updated}</strong> updated, <strong>{importResult.total_rows}</strong> total rows
                </div>
                {importResult.errors?.length > 0 && (
                  <div style={s.importErrList}>
                    {importResult.errors.map((e, i) => (
                      <div key={i} style={s.importErrItem}>Row {e.row} (IMO: {e.imo}): {e.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button style={s.importDismiss} onClick={() => setImportResult(null)}>✕</button>
          </div>
        )}

        {vessels.length > 0 && (
          <div style={s.statsBar}>
            <div style={s.statCard}>
              <div style={s.statVal}>{vessels.length}</div>
              <div style={s.statLbl}>TOTAL VESSELS</div>
            </div>
            <div style={s.statCard}>
              <div style={s.statVal}>{uniqueFlags.length}</div>
              <div style={s.statLbl}>FLAGS</div>
              <div style={s.statDtl}>{uniqueFlags.slice(0, 5).join(' · ') || '—'}</div>
            </div>
            <div style={s.statCard}>
              <div style={s.statVal}>{uniqueTypes.length}</div>
              <div style={s.statLbl}>VESSEL TYPES</div>
              <div style={s.statDtl}>{uniqueTypes.slice(0, 4).join(' · ') || '—'}</div>
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>⚓</div>
            <div style={s.emptyText}>{vessels.length === 0 ? 'No vessels registered yet.' : 'No vessels match your search.'}</div>
            {vessels.length === 0 && (
              <button style={s.addBtn} onClick={() => { setEditVessel(null); setPage('vessel-form'); }}>
                Add First Vessel
              </button>
            )}
          </div>
        ) : (
          <div style={s.table}>
            <div style={s.tableHeader}>
              <span>VESSEL NAME</span>
              <span>IMO</span>
              <span>FLAG</span>
              <span>TYPE</span>
              <span>DATA</span>
              <span>ACTIONS</span>
            </div>
            {filtered.map(v => {
              const { pct, missingRequired } = vesselCompleteness(v);
              const color = pct === 100 ? 'var(--green)' : pct >= 70 ? 'var(--gold)' : '#ff6b7a';
              return (
                <div key={v.id} style={s.tableRow}>
                  <span style={s.vesselName}>{v.ship_name}</span>
                  <span style={s.mono}>{v.imo_number}</span>
                  <span>{v.flag || '—'}</span>
                  <span>{v.ship_type || '—'}</span>
                  <span title={missingRequired.length ? `Missing: ${missingRequired.join(', ')}` : 'All required fields complete'}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '700', color }}>{pct}%</span>
                  </span>
                  <span style={s.actions}>
                    <button style={s.editBtn} onClick={() => { setEditVessel(v); setPage('vessel-form'); }}>Edit</button>
                    <button style={s.deleteBtn} onClick={() => handleDeleteVessel(v.imo_number)}>Delete</button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)', display: 'flex', flexDirection: 'column' },
  topAccent: { height: '3px', background: 'linear-gradient(90deg, var(--red) 0%, #c9a84c 60%, var(--red) 100%)' },
  loadingState: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--grey)', fontFamily: 'var(--font-mono)', fontSize: '14px',
  },

  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 32px', height: '60px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  headerIcon: { fontSize: '22px' },
  headerTitle: {
    fontSize: '13px', fontWeight: '700', letterSpacing: '2px',
    color: 'var(--red)', fontFamily: 'var(--font-mono)',
  },
  headerSub: { fontSize: '11px', color: 'var(--grey)', marginTop: '2px' },
  backBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  logoutBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },

  main: { flex: 1, padding: '32px 40px', overflowY: 'auto' },

  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { fontSize: '22px', fontWeight: '800', color: 'var(--white)' },
  pageSub: { fontSize: '13px', color: 'var(--grey)', marginTop: '4px' },
  topBarRight: { display: 'flex', gap: '12px', alignItems: 'center' },

  searchInput: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '10px 16px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', width: '260px', fontFamily: 'var(--font-mono)',
  },
  addBtn: {
    background: 'var(--red)', border: 'none', borderRadius: '8px',
    padding: '10px 20px', color: 'white', fontSize: '12px',
    fontWeight: '700', letterSpacing: '1px', cursor: 'pointer',
  },
  templateBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '8px 14px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '0.5px', cursor: 'pointer',
    fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
  },
  importBtn: {
    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)',
    color: '#60a5fa', padding: '8px 16px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px',
    cursor: 'pointer', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
  },
  overwriteLabel: {
    display: 'flex', alignItems: 'center', fontSize: '11px',
    color: 'var(--grey)', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
  },

  importSuccess: {
    background: 'rgba(0,196,140,0.08)', border: '1px solid rgba(0,196,140,0.3)',
    borderRadius: '10px', padding: '14px 18px', marginBottom: '20px',
    color: 'var(--green)', fontSize: '13px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  importError: {
    background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.3)',
    borderRadius: '10px', padding: '14px 18px', marginBottom: '20px',
    color: '#ff6b7a', fontSize: '13px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  importSummary: { marginBottom: '8px' },
  importErrList: { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' },
  importErrItem: { fontSize: '11px', color: '#ff6b7a', fontFamily: 'var(--font-mono)' },
  importDismiss: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    cursor: 'pointer', fontSize: '14px', padding: '0 0 0 12px', flexShrink: 0,
  },

  statsBar: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
  statCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '10px', padding: '16px 24px',
  },
  statVal: { fontSize: '28px', fontWeight: '800', color: '#c9a84c', fontFamily: 'var(--font-mono)', lineHeight: 1 },
  statLbl: { fontSize: '10px', color: 'var(--grey)', letterSpacing: '1.5px', fontFamily: 'var(--font-mono)', marginTop: '6px' },
  statDtl: { fontSize: '11px', color: 'var(--grey)', marginTop: '4px', opacity: 0.7, fontFamily: 'var(--font-mono)' },

  emptyState: { textAlign: 'center', padding: '80px 0' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyText: { fontSize: '16px', color: 'var(--grey)', marginBottom: '24px' },

  table: {
    background: 'var(--navy-mid)', borderRadius: '12px',
    overflow: 'hidden', border: '1px solid var(--navy-border)',
  },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px 1.4fr',
    padding: '14px 24px', background: 'rgba(255,255,255,0.03)',
    fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px',
    color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px 1.4fr',
    padding: '16px 24px', borderBottom: '1px solid var(--navy-border)',
    fontSize: '13px', alignItems: 'center',
  },
  vesselName: { fontWeight: '600', color: 'var(--white)' },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '12px' },
  actions: { display: 'flex', gap: '8px' },
  editBtn: {
    background: 'var(--gold-dim)', border: '1px solid rgba(201,168,76,0.3)',
    color: 'var(--gold)', padding: '5px 12px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  deleteBtn: {
    background: 'var(--red-glow)', border: '1px solid rgba(232,0,29,0.3)',
    color: '#ff6b7a', padding: '5px 12px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
};
